import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API } from '@/App';
import { calcHeadcount } from '@/lib/calcHeadcount';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { MapPin, Users, Calendar, Globe, ArrowRight } from 'lucide-react';

const CityPage = () => {
  const { city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCityData();
  }, [city]);

  const fetchCityData = async () => {
    try {
      const response = await axios.get(`${API}/community/${encodeURIComponent(city)}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch city data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 text-center">
          <h1 className="text-2xl font-bold">City not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-20 pb-20">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-primary to-accent py-20 px-4">
          <div className="max-w-6xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <MapPin className="w-5 h-5" />
                <span className="font-medium">Expat Community</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold mb-4">
                {data.city} Expats
              </h1>
              <p className="text-xl text-white/90 mb-8">
                Connect with fellow expats living in {data.city}
              </p>
              
              {/* Stats */}
              <div className="flex flex-wrap justify-center gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold">{data.member_count}</div>
                  <div className="text-white/80">Members</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold">{data.nationalities_count}</div>
                  <div className="text-white/80">Nationalities</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold">{data.event_count}</div>
                  <div className="text-white/80">Events</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Upcoming Events */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl border-2 border-border p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-heading font-bold">Upcoming Events</h2>
                  <Link to="/events">
                    <Button variant="outline" className="rounded-full">
                      View All <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                {data.upcoming_events?.length > 0 ? (
                  <div className="space-y-4">
                    {data.upcoming_events.map((event) => (
                      <Link key={event.event_id} to={`/events/${event.event_id}`}>
                        <div className="flex gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors">
                          <div className="w-16 h-16 bg-accent/10 rounded-xl flex flex-col items-center justify-center text-center">
                            <span className="text-xs text-muted-foreground">
                              {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' })}
                            </span>
                            <span className="text-xl font-bold text-primary">
                              {new Date(event.event_date).getDate()}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold mb-1">{event.title}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {event.event_time}
                              </span>
                              <span className="flex items-center">
                                <Users className="w-4 h-4 mr-1" />
                                {calcHeadcount(event)} attending
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No upcoming events in {data.city}</p>
                    <Link to="/create-event">
                      <Button className="mt-4 rounded-full">Create an Event</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Members */}
            <div>
              <div className="bg-white rounded-3xl border-2 border-border p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-bold">Members</h2>
                  <Link to="/find-expats">
                    <Button variant="ghost" size="sm" className="rounded-full">
                      See All
                    </Button>
                  </Link>
                </div>

                {data.members?.length > 0 ? (
                  <div className="space-y-4">
                    {data.members.slice(0, 8).map((member) => (
                      <Link key={member.user_id} to={`/profile/${member.user_id}`}>
                        <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white font-bold">
                            {member.name?.charAt(0) || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{member.name}</p>
                            <p className="text-xs text-muted-foreground flex items-center">
                              <Globe className="w-3 h-3 mr-1" />
                              {member.nationality}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No members yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityPage;
